import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { SOURCE_META } from "./fieldMap";

const show = (v) => (Array.isArray(v) ? v.join(" • ") : v == null || v === "" ? "—" : typeof v === "number" ? v.toFixed(1) : String(v));

// Selective acceptance of AI proposals — nothing is applied until the user confirms.
export default function ProposalDialog({ open, onOpenChange, proposals, onApply, firstRun, sources, title = "Review AI proposals", description, note, testid = "proposal-dialog" }) {
  const [sel, setSel] = useState(new Set());
  // pre-tick empty fields and fields previously filled by AI/policy; anything typed by the user (or of unknown origin) stays unticked
  useEffect(() => { if (open) setSel(new Set(proposals.filter((p) => firstRun || p.currentEmpty || ["extracted", "observed", "suggested", "scored", "policy"].includes((sources || {})[p.to])).map((p) => p.to))); }, [open, proposals, firstRun, sources]);
  const toggle = (k) => setSel((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#153E32]">{title}</DialogTitle>
          <DialogDescription>{description || "Tick what to accept. Fields you already edited are unticked by default and are never overwritten silently. “Read from label” = transcribed text; “AI draft” = wording that cannot be verified from a photo."}</DialogDescription>
        </DialogHeader>
        {note && <div className="rounded-lg border border-[#AD8642]/40 bg-[#F1E5C9]/60 px-3 py-2 text-xs text-[#153E32]" data-testid={`${testid}-note`}>{note}</div>}
        <div className="flex items-center gap-2 text-xs">
          <Button size="sm" variant="outline" onClick={() => setSel(new Set(proposals.map((p) => p.to)))} data-testid="proposal-select-all">Select all</Button>
          <Button size="sm" variant="outline" onClick={() => setSel(new Set())} data-testid="proposal-select-none">Clear</Button>
          <span className="text-[#6A776F]">{sel.size} of {proposals.length} selected</span>
        </div>
        <div className="divide-y divide-[#AD8642]/20 rounded-lg border border-[#AD8642]/25">
          {proposals.length === 0 && <div className="p-4 text-sm text-[#6A776F]">The AI reading matches the current content — nothing to apply.</div>}
          {proposals.map((p) => (
            <label key={p.to} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-[#F7F7F3]" data-testid={`proposal-${p.to.replace(/\./g, "-")}`}>
              <Checkbox checked={sel.has(p.to)} onCheckedChange={() => toggle(p.to)} className="mt-1" />
              <div className="min-w-0 flex-1 text-sm">
                <div className="flex items-center gap-2"><span className="font-semibold text-[#153E32]">{p.label}</span><Badge variant="outline" className={`text-[10px] ${SOURCE_META[p.kind].cls}`}>{SOURCE_META[p.kind].label}</Badge></div>
                {!p.currentEmpty && <div className="text-xs text-[#6A776F] line-through mt-0.5">{show(p.current)}</div>}
                <div className="text-[#153E32] mt-0.5">{show(p.proposed)}</div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="proposal-cancel">Cancel</Button>
          <Button onClick={() => onApply(sel)} disabled={!sel.size} className="bg-[#153E32] hover:bg-[#2A5144]" data-testid="proposal-apply">Apply {sel.size} selected</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
